import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const TeacherHelp = () => {
  const routes = all_routes;
  const [activeCategory, setActiveCategory] = useState("general");
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const faqData: FAQItem[] = [
    {
      id: "1",
      category: "general",
      question: "How do I take attendance for my classes?",
      answer:
        "Navigate to Dashboard and use the Smart Attendance Widget, or go to the Attendance Tracker from the menu. You can mark students present/absent and add notes for late arrivals.",
    },
    {
      id: "2",
      category: "general",
      question: "How can I create and assign homework?",
      answer:
        "Use the FAB button (+ icon) and select 'Add Homework', or go to Manage Homework from the menu. Set due dates, attach files, and track student submissions.",
    },
    {
      id: "3",
      category: "grades",
      question: "How do I enter grades for assignments?",
      answer:
        "Go to Grade Entry from the Tasks section or menu. Select your class, assignment, and enter grades. You can also provide feedback and notify parents automatically.",
    },
    {
      id: "4",
      category: "grades",
      question: "Can I edit grades after submitting them?",
      answer:
        "Yes, you can edit grades within the grade entry deadline set by your school. After that, you'll need admin approval for changes.",
    },
    {
      id: "5",
      category: "communication",
      question: "How do I send announcements to students and parents?",
      answer:
        "Use the FAB button and select 'Create Announcement', or go to Alert Parents. You can send to specific classes or all students, set priority levels, and track delivery.",
    },
    {
      id: "6",
      category: "communication",
      question: "How can I communicate with parents about student behavior?",
      answer:
        "Go to Menu > Report a Student for disciplinary issues, or use Alert Parents for general communication. You can also schedule parent-teacher meetings through the system.",
    },
    {
      id: "7",
      category: "schedule",
      question: "Where can I view my teaching schedule?",
      answer:
        "Your schedule is available in the Dashboard Next Class widget, or go to Menu > Full Schedule for a complete weekly view with all classes and periods.",
    },
    {
      id: "8",
      category: "schedule",
      question: "How do I request leave or report absence?",
      answer:
        "Go to Menu > Leave/Absence Requests to submit time-off requests. Include dates, reason, and any substitute teacher arrangements.",
    },
    {
      id: "9",
      category: "technical",
      question: "How do I reset my password?",
      answer:
        "Go to Menu > Settings > Security and use the 'Change Password' section. You'll need your current password to set a new one.",
    },
    {
      id: "10",
      category: "technical",
      question: "What should I do if I encounter technical issues?",
      answer:
        "Go to Menu > Report Technical Issues to submit a ticket with details about the problem. For urgent issues, contact the IT support team directly.",
    },
    {
      id: "11",
      category: "students",
      question: "How can I view detailed student information?",
      answer:
        "Go to Menu > Students Directory to browse students by class. Click on any student to view their profile, grades, attendance history, and parent contact information.",
    },
    {
      id: "12",
      category: "students",
      question: "How do I track homework submission rates?",
      answer:
        "In Manage Homework, you can see submission progress for each assignment. Use the analytics to identify students who need additional support.",
    },
  ];

  const categories = [
    { id: "general", name: "General", icon: "ti-home" },
    { id: "grades", name: "Grades & Assessment", icon: "ti-clipboard-list" },
    { id: "communication", name: "Communication", icon: "ti-message" },
    { id: "schedule", name: "Schedule & Classes", icon: "ti-calendar" },
    { id: "students", name: "Student Management", icon: "ti-users" },
    { id: "technical", name: "Technical Support", icon: "ti-settings" },
  ];

  const filteredFAQs = faqData.filter((faq) => {
    const categoryMatch = faq.category === activeCategory;
    const searchMatch =
      searchQuery === "" ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return categoryMatch && searchMatch;
  });

  const quickActions = [
    {
      title: "Contact Support",
      description: "Get help from our support team",
      icon: "ti-headphones",
      color: "primary",
      action: () => {
        /* Navigate to contact form */
      },
    },
    {
      title: "Video Tutorials",
      description: "Watch step-by-step guides",
      icon: "ti-video",
      color: "info",
      action: () => {
        /* Open video library */
      },
    },
    {
      title: "Download Manual",
      description: "Get the complete user guide",
      icon: "ti-download",
      color: "success",
      action: () => {
        /* Download PDF */
      },
    },
    {
      title: "Report Issue",
      description: "Submit a technical problem",
      icon: "ti-bug",
      color: "warning",
      action: () => {
        /* Navigate to issue form */
      },
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Help & Support</h3>
              <p className="text-muted mb-0">
                Find answers and get support for using the teacher portal
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="row mb-4">
            {quickActions.map((action, index) => (
              <div key={index} className="col-md-3 col-sm-6 mb-3">
                <div
                  className="card h-100 cursor-pointer"
                  onClick={action.action}
                >
                  <div className="card-body text-center">
                    <div
                      className={`avatar avatar-lg bg-${action.color}-transparent mx-auto mb-3`}
                    >
                      <i
                        className={`${action.icon} text-${action.color} fs-2`}
                      ></i>
                    </div>
                    <h6 className="mb-2">{action.title}</h6>
                    <p className="text-muted small mb-0">
                      {action.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="ti ti-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search help articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="row">
            {/* Categories */}
            <div className="col-md-3 mb-4">
              <div className="card">
                <div className="card-header">
                  <h6 className="card-title mb-0">Categories</h6>
                </div>
                <div className="card-body p-0">
                  <div className="list-group list-group-flush">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        className={`list-group-item list-group-item-action d-flex align-items-center ${
                          activeCategory === category.id ? "active" : ""
                        }`}
                        onClick={() => setActiveCategory(category.id)}
                      >
                        <i className={`${category.icon} me-2`}></i>
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Content */}
            <div className="col-md-9">
              <div className="card">
                <div className="card-header">
                  <h6 className="card-title mb-0">
                    {categories.find((cat) => cat.id === activeCategory)?.name}{" "}
                    - Frequently Asked Questions
                  </h6>
                </div>
                <div className="card-body">
                  {filteredFAQs.length > 0 ? (
                    <div className="accordion" id="faqAccordion">
                      {filteredFAQs.map((faq) => (
                        <div key={faq.id} className="accordion-item">
                          <h2 className="accordion-header">
                            <button
                              className={`accordion-button ${
                                expandedFAQ === faq.id ? "" : "collapsed"
                              }`}
                              type="button"
                              onClick={() =>
                                setExpandedFAQ(
                                  expandedFAQ === faq.id ? null : faq.id
                                )
                              }
                            >
                              {faq.question}
                            </button>
                          </h2>
                          <div
                            className={`accordion-collapse collapse ${
                              expandedFAQ === faq.id ? "show" : ""
                            }`}
                          >
                            <div className="accordion-body">{faq.answer}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <div className="avatar avatar-xl bg-light-transparent mx-auto mb-3">
                        <i className="ti ti-search fs-2"></i>
                      </div>
                      <h5>No results found</h5>
                      <p className="text-muted">
                        Try adjusting your search or browse other categories.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Support */}
          <div className="card">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-2">Still need help?</h5>
                  <p className="text-muted mb-0">
                    Can't find what you're looking for? Our support team is here
                    to help you.
                  </p>
                </div>
                <div className="col-md-4 text-md-end">
                  <Link to="/teacher/contact-admin" className="btn btn-primary">
                    <i className="ti ti-message me-1"></i>
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeacherHelp;
